import { cx } from 'class-variance-authority';
import noop from 'lodash/noop';
import { useEffect, useRef, useState } from 'react';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import type { VideoProps } from '@/app/components/Video';
import Video from '@/app/components/Video';
import { useBrowserFeatureContext } from '@/app/contexts/BrowserFeatureProvider';

import { PopHeroGradientBg } from './PopHeroGradientBg';

export interface PopHeroMediaProps {
  readonly onTriggerPlayVideo?: () => void;
}

export function PopHeroMedia({
  onTriggerPlayVideo = noop,
}: PopHeroMediaProps): JSX.Element {
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const playVideoTimeout = useRef<NodeJS.Timeout>();
  const { programmaticVideoPlayAllowed } = useBrowserFeatureContext();

  useEffect(() => {
    if (!playVideo) {
      clearTimeout(playVideoTimeout.current);
      playVideoTimeout.current = setTimeout(() => {
        onTriggerPlayVideo();
        setPlayVideo(true);
      }, 500);
    }
    return () => {
      clearTimeout(playVideoTimeout.current);
    };
  }, [onTriggerPlayVideo, playVideo]);

  const bgVideoProps: VideoProps = {
    'aria-hidden': 'true',
    autoPlay: programmaticVideoPlayAllowed === false,
    contentTitle: 'pop_hero',
    controls: false,
    className: 'size-full object-cover',
    location: 'pop_hero',
    muted: true,
    playVideo,
    playsInline: true,
    priority: true,
    type: 'video/mp4',
  };

  return (
    <Motion
      className="absolute left-0 top-0 z-0 size-full"
      data-cy="pop_hero_media"
      initial={{ opacity: 0 }}
      transition={{ duration: 1.5, type: 'tween' }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}
    >
      <ArtDirectionImage
        alt="pop_hero_background_alt"
        className="object-cover"
        fill
        srcSet={[
          {
            media: '(max-width: 1023px)',
            src: 'blue-sky/pop/pop_hero_bg_m.png?exp=-7',
          },
          {
            media: '(min-width: 1024px)',
            src: 'blue-sky/pop/pop_hero_bg_d.png?exp=-7',
          },
        ]}
      />
      <Video
        {...bgVideoProps}
        className={cx(
          bgVideoProps.className,
          'relative motion-safe:block motion-reduce:hidden',
        )}
        priority
        srcSet={[
          {
            media: '(min-width: 1024px)',
            src: 'blue-sky/pop/hero_bg_d.mp4',
          },
          {
            src: 'blue-sky/pop/hero_bg_m.mp4',
          },
        ]}
      />
      <PopHeroGradientBg />
    </Motion>
  );
}
